import Big from "big.js";
import { getLocale } from "./locale";
import {
  format as formatNumber,
  two_digit_number
} from "./number";
import {
  to_date as timestampToDate,
  to_string as timestampToString
} from "./timestamp";
export function localize(value, options = {}) {
  if (options.clean_zero && is_zero(value)) {
    return "0";
  }
  if (getLocale().time_format === "hours_minutes") {
    return to_hhmm(value);
  } else {
    return to_decimal(value).replace(".", getLocale().decimal_separator);
  }
}
export function localized_decimal(value) {
  const decimal_hours = to_decimal(value);
  if (decimal_hours) {
    return formatNumber(decimal_hours);
  } else {
    return decimal_hours;
  }
}
export function company_round(hours) {
  let round_to, rounding_mode;
  hours = Big(hours);
  const result = getLocale().rounding_style === "none" ? hours : (round_to = Big(getLocale().round_to), rounding_mode = getLocale().rounding_style === "round" ? 1 : 3, hours > 0 && hours <= round_to ? round_to : hours.div(round_to).round(0, rounding_mode).times(round_to));
  return Number(result);
}
export function to_decimal(value) {
  if (value === "") {
    return "";
  }
  if (typeof value === "number") {
    return (value + 0).toFixed(2);
  }
  if (value != null) {
    value = evaluate_any_math(value);
  }
  if (value != null && value.indexOf) {
    if (value.indexOf(":") === -1) {
      if (isNaN(parseFloat(value.replace(/,/, ".")))) {
        return "";
      }
      return parseFloat(value.replace(/,/, ".")).toFixed(2);
    }
    const time = to_hhmm(value).split(":");
    let negative = false;
    if (time[0].match(/^-/)) {
      negative = true;
      time[0] = time[0].sub("-", "");
    }
    const hours = time[0] === "" ? 0 : parseFloat(time[0]);
    const minutes = parseFloat(time[1] / 60);
    value = (minutes + 0 + hours).toFixed(2);
    if (isNaN(value)) {
      return "";
    }
    if (negative) {
      value = 0 - value;
    }
  }
  return value;
}
export function to_hhmm(value, options = {}) {
  if (value === void 0) {
    return void 0;
  }
  value = evaluate_any_math(value);
  if (is_hhmm(value)) {
    return normalize_hhmm(value, options);
  }
  if (isNaN(parseFloat(value))) {
    return "";
  }
  if (typeof value === "string") {
    value = parseFloat(value.replace(/,/, "."));
  }
  const hours_and_decimal_part = value.toFixed(2).split(".");
  let hours = hours_and_decimal_part[0] || "0";
  if (options.force_two_digit_hours) {
    hours = two_digit_number(hours);
  }
  const decimal_part = hours_and_decimal_part[1];
  const minutes = Math.round(parseFloat(decimal_part) * 60 / 100);
  return `${hours}:${two_digit_number(minutes)}`;
}
export function normalize_hhmm(value, options = {}) {
  value = (value.toString() || "").trim();
  if (!is_hhmm(value)) {
    return value;
  }
  if (value === "") {
    return "";
  }
  let [hours, minutes] = value.split(":");
  hours = parseInt((hours ?? "0").trim(), 10) || 0;
  minutes = parseInt((minutes ?? "0").trim(), 10) || 0;
  hours += Math.floor(minutes / 60);
  if (options.force_two_digit_hours) {
    hours = two_digit_number(hours);
  }
  minutes = minutes % 60;
  return `${hours}:${two_digit_number(minutes)}`;
}
export function is_zero(value) {
  return value === 0 || value === "0" || value === "0:00" || value === "0.00" || value === "0.0" || value === "0,0" || value === "0,00";
}
export function is_hhmm(value) {
  value = (value.toString() || "").trim();
  return value === "" || /^\d*:\d*$/.test(value);
}
export function duration_validation_errors(value) {
  if (value.match(/[^0-9\-:+ .,]/i)) {
    return "not numeric";
  }
  const numerical_value = parseFloat(to_decimal(value));
  if (numerical_value < 0 || value.match(/^\s*-/)) {
    return "negative";
  }
  return null;
}
export function elapsed_between(start, end) {
  if (!(start && end)) {
    return null;
  }
  if (!(start instanceof Date)) {
    start = new Date(start);
  }
  if (!(end instanceof Date)) {
    end = new Date(end);
  }
  let elapsed_milliseconds = end.getTime() - start.getTime();
  if (elapsed_milliseconds < 0) {
    elapsed_milliseconds += 1e3 * 60 * 60 * 24;
  }
  return elapsed_milliseconds / (1e3 * 3600);
}
export function ended_at_time(start, duration) {
  const started_at = timestampToDate(start);
  if (!started_at) {
    return null;
  }
  const hours = parseFloat(to_decimal(duration));
  const milliseconds = hours * 60 * 60 * 1e3;
  const end_time = new Date(started_at);
  end_time.setTime(started_at.getTime() + milliseconds);
  if (end_time.getSeconds() > 30) {
    end_time.setTime(end_time.getTime() + 30 * 1e3);
  }
  return timestampToString(end_time);
}
export function evaluate_any_math(number) {
  const operator_regexp = /\+|-/g;
  number = number.toString();
  const numbers = number.split(operator_regexp);
  const operators = number.match(operator_regexp);
  if (!operators) {
    return number;
  }
  if (operators.length === 1 && number.match(/^\s*[+|-]/)) {
    return number;
  }
  let total;
  if (getLocale().time_format === "hours_minutes") {
    const hours_minutes = to_hhmm(numbers.shift()).split(":");
    total = parseInt(hours_minutes[0], 10) * 60 + parseInt(hours_minutes[1], 10);
  } else {
    total = parseFloat(to_decimal(numbers.shift()));
  }
  for (let part of numbers) {
    if (getLocale().time_format === "hours_minutes") {
      const hours_minutes = to_hhmm(part).split(":");
      part = parseInt(hours_minutes[0], 10) * 60 + parseInt(hours_minutes[1], 10);
    } else {
      part = parseFloat(to_decimal(part));
    }
    const operator = operators.shift();
    if (operator === "-") {
      total -= part;
    }
    if (operator === "+") {
      total += part;
    }
  }
  if (getLocale().time_format === "hours_minutes") {
    let minutes = total % 60;
    const hours = (total - minutes) / 60;
    minutes = two_digit_number(minutes);
    return `${hours}:${minutes}`;
  } else {
    return to_decimal(total);
  }
}
export function sum_values(values) {
  if (getLocale().time_format === "hours_minutes") {
    const functor = function(memo, o) {
      let sign2 = 1;
      if (o.match(/^\s*-/)) {
        sign2 = -1;
      }
      const hh_mm = localize(o.replace(/^\s*\D/, "")).split(":");
      const hours2 = parseInt(hh_mm[0], 10);
      const minutes = parseInt(hh_mm[1], 10);
      return memo + sign2 * (hours2 * 60 + minutes);
    };
    const total_minutes = values.reduce(functor, 0);
    const abs_minutes = Math.abs(total_minutes);
    const hours = Math.floor(abs_minutes / 60);
    const remaining_minutes = two_digit_number(abs_minutes % 60);
    const sign = total_minutes < 0 ? "-" : "";
    return `${sign}${hours}:${remaining_minutes}`;
  } else {
    const functor = function(memo, o) {
      return memo + parseFloat(to_decimal(o));
    };
    const v = values.reduce(functor, 0);
    return localize(v);
  }
}
